import React from "react";

import { ExclamationCircleFilled } from "@ant-design/icons";
import { Button, Modal } from "antd";
import { useHistory, useLocation } from "react-router-dom";

import { useAccessStatus } from "../hooks/useAccessStatus";
import { useGraceInvoicePayment } from "../hooks/useGraceInvoicePayment";

import { canMakePayment } from "../../../utils/Permissions";
import { formatAmountDue, isFrozenExemptPath, parseDaysOverdue } from "../utils";

import { ReactComponent as LockIcon } from "../../../assets/images/common/LockIcon.svg";

import "./AccountFrozenModal.css";

export default function AccountFrozenModal() {
  const history = useHistory();
  const location = useLocation();
  const access = useAccessStatus();
  const { isPaymentBusy, startPayNow, startViewBillingDetails } = useGraceInvoicePayment();
  const allowPayment = canMakePayment();

  if (!access.isFrozen || isFrozenExemptPath(location.pathname)) {
    return null;
  }

  const amountDueLabel = formatAmountDue(access.invoice_amount_due);
  const daysSinceEnd = parseDaysOverdue(access.grace_days_overdue, access.grace_ends_on);

  return (
    <Modal
      open
      centered
      closable={false}
      maskClosable={false}
      footer={null}
      width={484}
      rootClassName="AccountFrozenModalRoot"
      wrapClassName="AccountFrozenModalWrap"
      className="AccountFrozenModal"
      zIndex={900}
      classNames={{
        content: "AccountFrozenModal__Content",
        body: "AccountFrozenModal__Main",
      }}
      styles={{
        mask: {
          background: "rgba(255, 255, 255, 0.45)",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
          inset: "68px 0 0 0",
        },
        wrapper: {
          inset: "68px 0 0 0",
        },
      }}
    >
      <div className="AccountFrozenModal__IconWrap">
        <span className="AccountFrozenModal__LockCircle" aria-hidden>
          <LockIcon className="AccountFrozenModal__LockIcon" />
        </span>
        <ExclamationCircleFilled className="AccountFrozenModal__Alert" />
      </div>

      <div className="AccountFrozenModal__Copy">
        <span className="AccountFrozenModal__Title">Account frozen due to unpaid renewal</span>
        <p className="AccountFrozenModal__Description">
          Your grace period has ended. Complete payment to restore submissions, results, reports, analytics, COAs, and other actions.
        </p>
      </div>

      {(amountDueLabel || daysSinceEnd != null) && (
        <div className="AccountFrozenModal__Facts">
          {amountDueLabel && (
            <div className="AccountFrozenModal__Fact">
              <span className="AccountFrozenModal__FactLabel">Amount due</span>
              <span className="AccountFrozenModal__FactValue AccountFrozenModal__FactValue--due">
                {amountDueLabel}
              </span>
            </div>
          )}
          {daysSinceEnd != null && (
            <div className="AccountFrozenModal__Fact">
              <span className="AccountFrozenModal__FactLabel">Grace period ended</span>
              <span className="AccountFrozenModal__FactValue">
                {daysSinceEnd === 0 ? "Today" : daysSinceEnd}
                {daysSinceEnd > 0 && (
                  <span className="AccountFrozenModal__FactSuffix">
                    {`${daysSinceEnd === 1 ? "day" : "days"} ago`}
                  </span>
                )}
              </span>
            </div>
          )}
        </div>
      )}

      <div className="AccountFrozenModal__Actions">
        {allowPayment && (
          <>
            <Button
              type="primary"
              className="AccountFrozenModal__PayNow"
              loading={isPaymentBusy}
              disabled={isPaymentBusy}
              onClick={startPayNow}
            >
              Pay now
            </Button>
            <Button
              type="default"
              className="AccountFrozenModal__Billing"
              loading={isPaymentBusy}
              disabled={isPaymentBusy}
              onClick={startViewBillingDetails}
            >
              View billing details
            </Button>
          </>
        )}
        <Button
          type="link"
          className="AccountFrozenModal__Support"
          disabled={isPaymentBusy}
          onClick={() => history.push("/myaccount/help")}
        >
          Contact support
        </Button>
      </div>
    </Modal>
  );
}
